// covered-by: frontends/carrie/src/ask-v2/review/__tests__/reviewCommentTags.test.ts
import { type AskPunttReviewComment } from "@mg/schemas/src/christo/askPunttReview";

/** Wire tag ids emitted on review comments (schema lands separately). */
export const REVIEW_COMMENT_TAG_IDS = [
  "nutrition",
  "completeness",
  "copy-diff",
  "code-values",
  "variants",
  "regulated-copy",
] as const;

export type ReviewCommentTagId = (typeof REVIEW_COMMENT_TAG_IDS)[number];

export type CommentWithOptionalTags = AskPunttReviewComment & {
  tags?: readonly string[];
};

const TAG_ALIAS: Record<string, ReviewCommentTagId> = {
  nutrition: "nutrition",
  completeness: "completeness",
  "copy-diff": "copy-diff",
  copy_diff: "copy-diff",
  "code-values": "code-values",
  code_values: "code-values",
  codes_values: "code-values",
  // why: warhol's reviewer and domain name, the one the wire actually carries.
  "codes-values": "code-values",
  variants: "variants",
  "regulated-copy": "regulated-copy",
  regulated_copy: "regulated-copy",
};

/**
 * Deterministic checks. They are not one of the six domains. A comment whose
 * only review tags are these matches Always-on checks, and nothing else.
 * Hyphenated slugs are what the comment carries; underscores are the pass names.
 */
export const ALWAYS_ON_TAG_IDS = [
  "line-diff",
  "diacritics",
  "typography",
  "correspondence",
  "identifier",
  "barcode-zone",
  "dangling-field",
  "mandatory-copy",
  "enumeration-order",
  "consistency",
] as const;

export type AlwaysOnTagId = (typeof ALWAYS_ON_TAG_IDS)[number];

const ALWAYS_ON_ALIAS: Record<string, AlwaysOnTagId> = {
  "line-diff": "line-diff",
  line_diff: "line-diff",
  diacritics: "diacritics",
  typography: "typography",
  correspondence: "correspondence",
  identifier: "identifier",
  identifiers: "identifier",
  "barcode-zone": "barcode-zone",
  barcode_zone: "barcode-zone",
  "dangling-field": "dangling-field",
  dangling_field: "dangling-field",
  "mandatory-copy": "mandatory-copy",
  mandatory_copy: "mandatory-copy",
  "enumeration-order": "enumeration-order",
  enumeration_order: "enumeration-order",
  consistency: "consistency",
};

/** The prefix warhol writes on a finding an expert's method raised. */
export const EXPERT_TAG_PREFIX = "expert:";

/** One expert's review, as the "raised by" filter keys it: `expert:<userId>`. */
export type ExpertFilterKey = `${typeof EXPERT_TAG_PREFIX}${string}`;

/** The AI findings no expert's method raised: Puntt's own reviewers and checks. */
export const PUNTT_FINDINGS_SOURCE = "puntt";

/** Who raised a finding, as the "raised by" filter toggles it. */
export type ReviewCommentSourceKey =
  | ExpertFilterKey
  | typeof PUNTT_FINDINGS_SOURCE;

/** The "raised by" filter's starting state: nothing hidden. */
export const NO_HIDDEN_SOURCES: ReadonlySet<ReviewCommentSourceKey> =
  new Set<ReviewCommentSourceKey>();

export type ExpertFilterOption = {
  key: ExpertFilterKey;
  expertId: string;
  /** The expert's name when the page knows it; the filter falls back to a generic label. */
  name?: string;
};

/** i18n keys under askV2.review.commentTags.* */
export function reviewCommentTagLabelKey(
  tag: ReviewCommentTagId,
): `askV2.review.commentTags.${ReviewCommentTagId}` {
  return `askV2.review.commentTags.${tag}`;
}

export function normalizeReviewCommentTag(
  value: string,
): ReviewCommentTagId | null {
  const trimmed = value.trim().toLowerCase();
  if (!trimmed) return null;
  return TAG_ALIAS[trimmed] ?? null;
}

export function reviewCommentTags(
  comment: CommentWithOptionalTags,
): ReviewCommentTagId[] {
  const raw = comment.tags ?? [];
  const normalized = raw
    .map((tag) => normalizeReviewCommentTag(tag))
    .filter((tag): tag is ReviewCommentTagId => tag != null);
  return Array.from(new Set(normalized));
}

export function normalizeAlwaysOnTag(value: string): AlwaysOnTagId | null {
  const trimmed = value.trim().toLowerCase();
  if (!trimmed) return null;
  return ALWAYS_ON_ALIAS[trimmed] ?? null;
}

export function reviewCommentAlwaysOnTags(
  comment: CommentWithOptionalTags,
): AlwaysOnTagId[] {
  const normalized = (comment.tags ?? [])
    .map((tag) => normalizeAlwaysOnTag(tag))
    .filter((tag): tag is AlwaysOnTagId => tag != null);
  return Array.from(new Set(normalized));
}

/** A finding the six domains do not name: only an always-on check raised it. */
export function isAlwaysOnOnlyComment(
  comment: CommentWithOptionalTags,
): boolean {
  return (
    reviewCommentAlwaysOnTags(comment).length > 0 &&
    reviewCommentTags(comment).length === 0
  );
}

export function alwaysOnTagLabelKey(
  tag: AlwaysOnTagId,
): `reviewPreflight.alwaysOn.${AlwaysOnTagId}.name` {
  return `reviewPreflight.alwaysOn.${tag}.name`;
}

/** The experts whose methods raised this finding, as filter keys. */
export function reviewCommentExpertKeys(
  comment: CommentWithOptionalTags,
): ExpertFilterKey[] {
  const keys = (comment.tags ?? [])
    .map((tag) => tag.trim().toLowerCase())
    .filter(
      (tag): tag is ExpertFilterKey =>
        tag.startsWith(EXPERT_TAG_PREFIX) &&
        tag.length > EXPERT_TAG_PREFIX.length,
    );
  return Array.from(new Set(keys));
}

/**
 * One filter entry per expert whose method raised a finding in this review,
 * in the order their findings first appear.
 */
export function reviewExpertFilterOptions(
  comments: readonly CommentWithOptionalTags[],
  nameByUserId: Readonly<Record<string, string>> = {},
): ExpertFilterOption[] {
  const seen = new Set<ExpertFilterKey>();
  const options: ExpertFilterOption[] = [];
  for (const comment of comments) {
    if (comment.isDeleted) continue;
    for (const key of reviewCommentExpertKeys(comment)) {
      if (seen.has(key)) continue;
      seen.add(key);
      const expertId = key.slice(EXPERT_TAG_PREFIX.length);
      const name = nameByUserId[expertId]?.trim();
      options.push({ key, expertId, ...(name ? { name } : {}) });
    }
  }
  return options;
}

export function commentMatchesActiveTags(
  comment: CommentWithOptionalTags,
  activeTags: ReadonlySet<ReviewCommentTagId>,
): boolean {
  const tags = reviewCommentTags(comment);
  if (tags.length === 0) return true;
  return tags.some((tag) => activeTags.has(tag));
}

/** Who raised this finding; empty for a comment a person wrote. */
export function reviewCommentSources(
  comment: CommentWithOptionalTags,
): ReviewCommentSourceKey[] {
  const experts = reviewCommentExpertKeys(comment);
  if (experts.length > 0) return experts;
  return comment.isAI ? [PUNTT_FINDINGS_SOURCE] : [];
}

/**
 * Whether the "raised by" filter keeps this finding: hidden only when every
 * source that raised it is hidden.
 *
 * why a hidden set, not an active one: an expert's entry appears only once
 * their findings load, and a new entry must start shown.
 */
export function commentMatchesShownSources(
  comment: CommentWithOptionalTags,
  hiddenSources: ReadonlySet<ReviewCommentSourceKey>,
): boolean {
  const sources = reviewCommentSources(comment);
  if (sources.length === 0) return true;
  return sources.some((source) => !hiddenSources.has(source));
}

function matchesCheckedCategory(
  comment: CommentWithOptionalTags,
  activeTags: ReadonlySet<ReviewCommentTagId>,
): boolean {
  return reviewCommentTags(comment).some((tag) => activeTags.has(tag));
}

function matchesCheckedSource(
  comment: CommentWithOptionalTags,
  hiddenSources: ReadonlySet<ReviewCommentSourceKey>,
): boolean {
  // The caller already returns before this for a comment with no raiser.
  return commentMatchesShownSources(comment, hiddenSources);
}

export function isReviewCommentVisible(
  comment: CommentWithOptionalTags,
  activeTags: ReadonlySet<ReviewCommentTagId>,
  hiddenSources: ReadonlySet<ReviewCommentSourceKey> = NO_HIDDEN_SOURCES,
  alwaysOn = true,
  // The raised-by boxes exist only once an expert's findings are in the review.
  // Until then Puntt is not a checked box, so a category untick still hides.
  sourceFilterOn = false,
): boolean {
  if (comment.isDeleted) return false;
  // A person's comment is not one of the boxes.
  if (reviewCommentSources(comment).length === 0) return true;
  // why: boxes are a union — a category requirement on top of his review hides his findings
  if (
    matchesCheckedCategory(comment, activeTags) ||
    (alwaysOn && isAlwaysOnOnlyComment(comment)) ||
    (sourceFilterOn && matchesCheckedSource(comment, hiddenSources))
  ) {
    return true;
  }
  // Nothing names an untagged finding, so it stays until a raiser box hides it.
  if (
    reviewCommentTags(comment).length === 0 &&
    !isAlwaysOnOnlyComment(comment)
  ) {
    return !sourceFilterOn;
  }
  return false;
}

export type SavedReviewCommentFilter = {
  activeTags: readonly ReviewCommentTagId[];
  alwaysOn: boolean;
  hiddenSources: readonly ReviewCommentSourceKey[];
};

/** The wire filter uses plain strings. Keep only tags and sources the list can toggle. */
export function savedReviewCommentFilter(
  saved:
    | {
        activeTags: readonly string[];
        alwaysOn: boolean;
        hiddenSources: readonly string[];
      }
    | null
    | undefined,
): SavedReviewCommentFilter | undefined {
  if (saved == null) return undefined;
  return {
    activeTags: saved.activeTags
      .map((tag) => normalizeReviewCommentTag(tag))
      .filter((tag): tag is ReviewCommentTagId => tag != null),
    alwaysOn: saved.alwaysOn,
    hiddenSources: saved.hiddenSources.filter(
      (source): source is ReviewCommentSourceKey =>
        source === PUNTT_FINDINGS_SOURCE ||
        (source.startsWith(EXPERT_TAG_PREFIX) &&
          source.length > EXPERT_TAG_PREFIX.length),
    ),
  };
}

export function expertUserIdFromSelection(id: string): string | null {
  if (id.startsWith("expert-method:")) return id.slice("expert-method:".length);
  if (id.startsWith("expert:")) return id.slice("expert:".length);
  return null;
}

/**
 * The filter a viewer opens on. The runner starts on the reviews she picked,
 * with always-on off. Puntt's review starts checked only when every standard
 * category was in that scope. Anyone else starts on every review, including
 * always-on. A saved filter for this viewer replaces that default.
 */
export function deriveReviewCommentFilter(input: {
  viewerUserId?: string;
  startedBy?: string | null;
  selectedReviewerIds?: readonly string[];
  selectedExpertIds?: readonly string[];
  saved?: SavedReviewCommentFilter | null;
  expertKeys?: readonly ExpertFilterKey[];
}): {
  activeTags: Set<ReviewCommentTagId>;
  alwaysOn: boolean;
  hiddenSources: Set<ReviewCommentSourceKey>;
} {
  if (input.saved) {
    return {
      activeTags: new Set(input.saved.activeTags),
      alwaysOn: input.saved.alwaysOn,
      hiddenSources: new Set(input.saved.hiddenSources),
    };
  }
  const isRunner =
    input.startedBy != null &&
    input.startedBy !== "" &&
    input.startedBy === input.viewerUserId;
  if (!isRunner) {
    return {
      activeTags: defaultActiveReviewCommentTags(),
      alwaysOn: true,
      hiddenSources: new Set(),
    };
  }
  const activeTags = new Set<ReviewCommentTagId>();
  for (const id of input.selectedReviewerIds ?? []) {
    const tag = normalizeReviewCommentTag(id);
    if (tag) activeTags.add(tag);
  }
  const selectedExperts = new Set(
    (input.selectedExpertIds ?? [])
      .map((id) => expertUserIdFromSelection(id))
      .filter((id): id is string => id != null && id !== ""),
  );
  const hiddenSources = new Set<ReviewCommentSourceKey>();
  const scopeRanEveryCategory = REVIEW_COMMENT_TAG_IDS.every((id) =>
    activeTags.has(id),
  );
  // why: Puntt's review matches every AI finding no expert raised, which is
  // the six categories, the always-on checks, and findings with neither tag.
  // On a partial scope that box also shows categories and checks she did not
  // run. It stays on only when every category row was in the scope.
  if (!scopeRanEveryCategory) hiddenSources.add(PUNTT_FINDINGS_SOURCE);
  for (const key of input.expertKeys ?? []) {
    const userId = key.slice(EXPERT_TAG_PREFIX.length);
    if (!selectedExperts.has(userId)) hiddenSources.add(key);
  }
  return { activeTags, alwaysOn: false, hiddenSources };
}

export function defaultActiveReviewCommentTags(): Set<ReviewCommentTagId> {
  return new Set(REVIEW_COMMENT_TAG_IDS);
}
